import {
  usePerson,
  useLoanApplication,
  useApplicationStatusUtils,
} from '@axo/shared/data-access/hooks';
import { permissions } from '@axo/shared/data-access/types';
import { Button } from '@blueprintjs/core';
import { Avatar } from '../Avatar';
import styles from './PersonInfoSection.module.css';
import { AceTextField } from '../../forms/generic-fields/AceTextField.tsx';
import { AceEmailField } from '../../forms/generic-fields/AceEmailField.tsx';
import { useApplicantsFormContext } from '../../forms/applicants/context.ts';
import { ConditionalTooltip } from '../ConditionalTooltip/ConditionalTooltip.tsx';
import { useHasAnyOfRoles } from '@axo/shared/data-access/provider';
import { AceGenericFieldLayout } from '../../forms/generic-fields/AceGenericFieldLayout.tsx';
import { AcePhoneNumberField } from '../../forms/generic-fields/AcePhoneNumberField.tsx';
import { useApplicantsFormStore } from '../../forms/applicants/store.ts';
import { renderPhoneNumber } from '@axo/shared/util/formatters';
import { CopyTextButton } from '../CopyTextButton/CopyTextButton.tsx';
import { Product } from '@axo/shared/data-access/types/loan_quote';
import { MarketCountryCode } from '@axo/shared/data-access/types/ace_index';
import { useConfirmDialog } from '../../hooks/utils/useConfirmDialog.ts';
import { AceCheckboxField } from '../../forms/generic-fields/AceCheckboxField.tsx';
import { useTypedFlags } from '@axo/shared/services/feature-flags';
import { ACEFlags } from '../../utils/ACEFlags.ts';
import { AceSelectField } from '../../forms/generic-fields/AceSelectField.tsx';
import { useFilteredAddressTypes } from './useFilteredAddressTypes.ts';
import { ApplicationConditionalWrapper } from '../ConditionalWrappers/ApplicationConditionalWrapper.tsx';
import { AceNumberField } from '../../forms/generic-fields/AceNumberField.tsx';

export type IPersonInfo = {
  applicationID: string;
  personID: string;
};

export function PersonInfoSection({ applicationID, personID }: IPersonInfo) {
  const { data: personData } = usePerson(personID);
  const { data: applicationData } = useLoanApplication(applicationID);
  const { formState, control, watch, reset, submit, trigger, setValue } =
    useApplicantsFormContext(applicationID);
  const {
    isEditing,
    actions: { setIsEditing },
  } = useApplicantsFormStore(applicationID);
  const { isFinalApplicationStatus } = useApplicationStatusUtils();
  const { confirm } = useConfirmDialog();

  const isCoApplicant =
    personID === applicationData?.CoApplicantPersonID || personID === '';
  const applicantExists = !isCoApplicant || !!personID;

  const applicantKey = isCoApplicant ? 'CoApplicant' : 'MainApplicant';

  const hasEditPermissions = useHasAnyOfRoles([
    permissions.RoleName.Sales,
    permissions.RoleName.SecuredSales,
    permissions.RoleName.Telemarketing,
    permissions.RoleName.Manager,
  ]);

  const filteredAddressTypes = useFilteredAddressTypes();

  const { flag_allow_add_co_applicant, flag_change_address_type } =
    useTypedFlags(ACEFlags);

  const age = personData?.Age;
  const sex = personData?.Sex;

  const [
    zipCode,
    city,
    street,
    streetNumber,
    streetLevel,
    streetDoorSide,
    deceased,
  ] = watch([
    `${applicantKey}.Person.ZipCode`,
    `${applicantKey}.Person.City`,
    `${applicantKey}.Person.Street`,
    `${applicantKey}.Person.StreetNumber`,
    `${applicantKey}.Person.StreetLevel`,
    `${applicantKey}.Person.StreetDoorSide`,
    `${applicantKey}.Person.Deceased`,
  ]);

  const address1 = [zipCode, city].filter(Boolean).join(' ');
  const address2 = [street, streetNumber].filter(Boolean).join(' ');
  const address3 = [streetLevel, streetDoorSide].filter(Boolean).join(' ');
  const fullAddress = (
    [address1, address2].filter(Boolean).join(', ') +
    (address3 ? ` (${address3})` : '')
  ).trim();

  return (
    <div className={styles.fieldsContainer}>
      <div className={styles.mainLayout}>
        <div className={styles.horizontalStack}>
          <div className={styles.fieldsContainer}>
            <AceTextField
              editable={isEditing && applicantExists}
              control={control}
              name={`${applicantKey}.Person.FirstName`}
              label="First name:"
              formatter={(value) => (
                <strong>{value ? String(value) : '-'}</strong>
              )}
            />
            <AceTextField
              editable={isEditing && applicantExists}
              control={control}
              name={`${applicantKey}.Person.LastName`}
              label="Last name:"
              formatter={(value) => (
                <strong>{value ? String(value) : '-'}</strong>
              )}
            />
          </div>
          <div className={styles.fieldsContainerSmall}>
            <AceGenericFieldLayout
              label="Sex:"
              valueElement={<strong>{sex || '-'}</strong>}
              inputElement={null}
            />
            <AceGenericFieldLayout
              label="Age:"
              valueElement={<strong>{age || '-'}</strong>}
              inputElement={null}
            />
          </div>
        </div>
        <span className={styles.avatarContainer}>
          <Avatar age={age} sex={sex} className={styles.avatar} />
          {personData?.Fraud && <div className={styles.stamp}>FRAUD</div>}
        </span>
      </div>
      <div className={styles.fieldsContainer}>
        {!isEditing && (
          <AceGenericFieldLayout
            label="Address:"
            valueElement={<strong>{fullAddress || '-'}</strong>}
            inputElement={null}
          />
        )}
        {isEditing && (
          <>
            {flag_change_address_type && filteredAddressTypes.length > 0 && (
              <AceSelectField
                editable={isEditing && applicantExists}
                control={control}
                name={`${applicantKey}.Person.AddressType`}
                label="Address type:"
                formatter={(value) => (
                  <strong>{value ? String(value) : '-'}</strong>
                )}
                options={[
                  ...Object.values(filteredAddressTypes).map((value) => ({
                    value,
                  })),
                ]}
              />
            )}
            <AceTextField
              editable={isEditing && applicantExists}
              control={control}
              name={`${applicantKey}.Person.ZipCode`}
              label="Zip code:"
              formatter={(value) => (
                <strong>{value ? String(value) : '-'}</strong>
              )}
            />
            <AceTextField
              editable={isEditing && applicantExists}
              control={control}
              name={`${applicantKey}.Person.City`}
              label="City:"
              formatter={(value) => (
                <strong>{value ? String(value) : '-'}</strong>
              )}
            />
            <AceTextField
              editable={isEditing && applicantExists}
              control={control}
              name={`${applicantKey}.Person.Street`}
              label="Street:"
              formatter={(value) => (
                <strong>{value ? String(value) : '-'}</strong>
              )}
            />
            {applicationData?.MarketCountry === MarketCountryCode.Denmark && (
              <>
                <AceTextField
                  editable={isEditing && applicantExists}
                  control={control}
                  name={`${applicantKey}.Person.StreetNumber`}
                  label="Street number:"
                  formatter={(value) => (
                    <strong>{value ? String(value) : '-'}</strong>
                  )}
                />
                <AceTextField
                  editable={isEditing && applicantExists}
                  control={control}
                  name={`${applicantKey}.Person.StreetLevel`}
                  label="Street level:"
                  formatter={(value) => (
                    <strong>{value ? String(value) : '-'}</strong>
                  )}
                />
                <AceTextField
                  editable={isEditing && applicantExists}
                  control={control}
                  name={`${applicantKey}.Person.StreetDoorSide`}
                  label="Street door side:"
                  formatter={(value) => (
                    <strong>{value ? String(value) : '-'}</strong>
                  )}
                />
              </>
            )}
          </>
        )}
        <AceTextField
          editable={isEditing && !applicantExists}
          control={control}
          name={`${applicantKey}.Person.SocialSecurityNumber`}
          label="SSN:"
          formatter={(value) => <strong>{value ? String(value) : '-'}</strong>}
        />
        {!isCoApplicant && (
          <>
            <ApplicationConditionalWrapper
              whitelist={[
                {
                  productType: [Product.UnsecuredLoan],
                  marketCountry: [MarketCountryCode.Denmark],
                },
              ]}
            >
              <AceNumberField
                editable={isEditing}
                control={control}
                name="LoanApplication.RegistrationNumber"
                label="Registration number:"
                formatter={(value) => (
                  <strong>{value ? String(value) : '-'}</strong>
                )}
              />
            </ApplicationConditionalWrapper>
            <ApplicationConditionalWrapper
              whitelist={[
                {
                  productType: [Product.UnsecuredLoan],
                  marketCountry: [MarketCountryCode.Sweden],
                },
              ]}
            >
              <AceTextField
                editable={isEditing}
                control={control}
                name="LoanApplication.AccountClearingNumber"
                label="Clearing number:"
                formatter={(value) => (
                  <strong>{value ? String(value) : '-'}</strong>
                )}
              />
            </ApplicationConditionalWrapper>
            <ApplicationConditionalWrapper
              whitelist={[
                {
                  productType: [Product.UnsecuredLoan],
                  marketCountry: [
                    MarketCountryCode.Sweden,
                    MarketCountryCode.Finland,
                    MarketCountryCode.Denmark,
                  ],
                },
              ]}
            >
              <AceTextField
                editable={isEditing}
                control={control}
                name="LoanApplication.AccountNumber"
                label="Account number:"
                formatter={(value) => (
                  <strong>{value ? String(value) : '-'}</strong>
                )}
              />
            </ApplicationConditionalWrapper>
          </>
        )}
        <AcePhoneNumberField
          editable={isEditing}
          control={control}
          countryCodeName={`${applicantKey}.Customer.MobilePhone.CountryCode`}
          phoneNumberName={`${applicantKey}.Customer.MobilePhone.Number`}
          label="Phone:"
          formatter={(phoneCountryCode, phoneNumber) => {
            const formattedPhoneNumber = renderPhoneNumber({
              phoneCountryCode: phoneCountryCode
                ? String(phoneCountryCode)
                : undefined,
              phoneNumber: phoneNumber ? String(phoneNumber) : undefined,
            });

            return (
              <strong>
                <CopyTextButton value={formattedPhoneNumber}>
                  {formattedPhoneNumber}
                </CopyTextButton>
              </strong>
            );
          }}
        />
        <AceEmailField
          editable={isEditing}
          control={control}
          name={`${applicantKey}.Customer.Email`}
          label="Email:"
          formatter={(value) => <strong>{value ? String(value) : '-'}</strong>}
          warning={
            isEditing
              ? "Changing email may give the new email access to MyPage across all the customer's active applications. Change with caution."
              : undefined
          }
        />
        {(isEditing || deceased) && (
          <AceCheckboxField
            editable={isEditing}
            control={control}
            name={`${applicantKey}.Person.Deceased`}
            label="Deceased:"
            formatter={(value) => <strong>{value ? 'Yes' : 'No'}</strong>}
          />
        )}
      </div>
      <div className={styles.actionsContainer}>
        {isEditing && (
          <ConditionalTooltip
            conditions={[]}
            render={(disabled) => (
              <Button
                disabled={disabled}
                icon="cross"
                text="Discard changes"
                onClick={() => {
                  reset();
                  trigger();
                  setIsEditing(false);
                }}
              />
            )}
          />
        )}
        {isEditing && (
          <ConditionalTooltip
            conditions={[
              {
                isTooltipVisible: !formState.isDirty,
                tooltipContent: 'Nothing to save',
                isChildDisabled: true,
              },
              {
                isTooltipVisible: !formState.isValid,
                tooltipContent: 'Some fields have validation errors',
              },
            ]}
            render={(disabled) => (
              <Button
                disabled={disabled}
                loading={formState.isSubmitting}
                intent={!formState.isValid ? 'danger' : 'primary'}
                icon="floppy-disk"
                text="Save"
                onClick={() => submit()}
              />
            )}
          />
        )}

        {!isEditing && isCoApplicant && (
          <ConditionalTooltip
            conditions={[
              {
                isTooltipVisible: isFinalApplicationStatus(
                  applicationData?.Status ?? undefined,
                ),
                tooltipContent: "Can't remove co-applicant on final statuses",
                isChildDisabled: true,
              },
              {
                isTooltipVisible: !flag_allow_add_co_applicant,
                tooltipContent: 'This feature is currently disabled',
                isChildDisabled: true,
              },
            ]}
            render={(disabled) => (
              <Button
                loading={formState.isSubmitting}
                disabled={disabled}
                icon="trash"
                intent="danger"
                text="Remove co-applicant"
                onClick={() =>
                  confirm({
                    confirmText: 'Remove',
                    cancelText: 'Cancel',
                    message:
                      'Are you sure you want to remove the co-applicant from this application?',
                    confirmCallback: () => {
                      setValue('CoApplicant', undefined);
                      submit();
                    },
                  })
                }
              />
            )}
          />
        )}

        {!isEditing && (
          <ConditionalTooltip
            conditions={[
              {
                isTooltipVisible: !hasEditPermissions,
                tooltipContent: "You don't have permissions to edit applicants",
                isChildDisabled: true,
              },
            ]}
            render={(disabled) => (
              <Button
                loading={formState.isSubmitting}
                disabled={disabled}
                icon="edit"
                text="Edit"
                onClick={() => setIsEditing(true)}
              />
            )}
          />
        )}
      </div>
    </div>
  );
}
